/*!
 * Copyright 2021 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

// eslint-disable-next-line max-len
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  LOCALE_ID,
  Output,
} from "@angular/core";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {TherapyTemplateGroup} from "@trec/api/drug-store";

export const THERAPY_TEMPLATE_SELECTOR_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => TherapyTemplateSelectorComponent),
  multi: true,
};

@Component({
  selector: "c3-therapy-template-selector",
  templateUrl: "./therapy-template-selector.component.html",
  styleUrls: ["./therapy-template-selector.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [THERAPY_TEMPLATE_SELECTOR_VALUE_ACCESSOR],
  standalone: false
})
export class TherapyTemplateSelectorComponent implements ControlValueAccessor {
  // IO ///////////////////////////////////////////////////////////////////////////////////////////

  @Input()
  searchPending: boolean;

  @Input()
  searchResults: TherapyTemplateGroup[] | undefined;

  @Output()
  search: EventEmitter<string | null> = new EventEmitter();

  @Output()
  selected = new EventEmitter<TherapyTemplateGroup | null>();

  // Form control value accessor //////////////////////////////////////////////////////////////////

  /**
   * Angular control's on change function.
   */
  onChange?: (data: TherapyTemplateGroup | null) => void;

  /**
   * Angular control's on touched function.
   */
  onTouched?: () => void;

  disabled = false;

  // Other variables //////////////////////////////////////////////////////////////////////////////

  /**
   * Value displayed in search ttg field.
   */
  ttgSearchValue = "";

  /**
   * Current vieweed and selected tt.
   */
  currentTtg?: TherapyTemplateGroup | null;

  constructor(private cd: ChangeDetectorRef, @Inject(LOCALE_ID) public localeId: string) { }

  onTtSelected(tt: TherapyTemplateGroup | null = null) {
    this.currentTtg = tt;
    this.triggerControlValueAccessorChange(tt);
    this.cd.markForCheck();
  }

  onDoSearch(value: string | TherapyTemplateGroup) {
    if (typeof value === "string") {
      // Search for tt
      this.search.emit(value);
    } else {
      // Tt has been selected
      this.onTtSelected(value);
    }
  }

  // Control value accessor methods ///////////////////////////////////////////////////////////////

  writeValue(tt: TherapyTemplateGroup | null = null): void {
    this.currentTtg = tt;
    this.cd.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  private triggerControlValueAccessorChange(data: TherapyTemplateGroup | null) {
    this.onTouched?.();
    this.onChange?.(data);
    this.selected.emit(data);
  }
}
